import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { convertInsuranceType, convertInsuranceTypeBack, convertLongestTripDuration, convertLongestTripDurationBack, convertTravelDestination, convertTravelDestinationBack, getDefaultCoverageRange, i18n, LongestTripDurationEnum, openCallMeModal, showMessage, TravelDestinationEnum, TravelInsuranceTypeEnum, useCreateSoWithCaptcha, useForm, useGeneralSoContext, useGetSo, useLoader } from "src/common";
import { isRateLimitDenial } from "src/common/apiGraphql/captcha";
import { GetSoDto, SoTravelTravelData, TravelInsuranceType } from "src/common/apiGraphql/types/types";
import {
  ArrowRightIcon,
  BasicDataForm,
  BasicDataFormValues,
  Button,
  CaptchaChallenge,
  Card,
  ContainerComponent,
  Flex,
  RequestAgentCard,
  Stepper,
  Text
} from "src/components";
import { colors } from "src/theme";
import { AppRoutes } from "./_router/app.routes";
import { Helmet } from "react-helmet-async";


const BasicDataPage = () => {
  const { formRef, submitForm } = useForm();
  const navigate = useNavigate();
  const { so, loading, refetch } = useGetSo();
  const { submit: createSo, captcha } = useCreateSoWithCaptcha({ page: 'basic-data' });
  const { setSoInitialData, soInitialData } = useGeneralSoContext();
  const { startLoader, stopLoader } = useLoader();

  const { soId: soIdFromUrl } = useParams();
  const { setSoId } = useGeneralSoContext();

  useEffect(() => {
    if (soIdFromUrl) {
      setSoId(soIdFromUrl);
    }
  }, [soIdFromUrl]);

  useEffect(() => {
    if (so) {
      refetch();
    }
  }, []);

  const handleFormSubmit = async (values: BasicDataFormValues) => {
    startLoader();
    const data: SoTravelTravelData = formToModel(values);

    if (data.insuranceType === TravelInsuranceType.Single) {
      data.longestTripDuration = undefined;
    }

    setSoInitialData(data);

    if (so) {
      // A CAPTCHA_REQUIRED answer is handled by the hook: it renders the widget below the
      // form and resubmits this exact payload with the token, then runs these handlers.
      createSo(
        {
          travelData: data,
          email: so?.email || '',
          soId: so?._id || '',
          source: so?.source || undefined,
        },
        {
          onSuccess: () => navigate(AppRoutes.WEBSHOP.STEP_2),
          onError: (error) => {
            // RATE_LIMITED / IP_BLOCKED are already toasted by the Apollo error link.
            if (isRateLimitDenial(error)) return;
            showMessage({
              type: 'error',
              message: i18n.t('common.messages.error'),
              duration: 5000,
              fixed: true
            });
          },
          onSettled: () => stopLoader(),
        }
      );
    } else {
      navigate(AppRoutes.WEBSHOP.STEP_2);
      stopLoader();
    }
  };

  const getInsuranceType = (so?: GetSoDto, soInitialData?: SoTravelTravelData): TravelInsuranceTypeEnum => {
    if (so?.productTypeSpecific?.travelData?.insuranceType) {
      return convertInsuranceTypeBack(so?.productTypeSpecific?.travelData?.insuranceType);
    } else if (soInitialData?.insuranceType) {
      return convertInsuranceTypeBack(soInitialData?.insuranceType);
    } else {
      return TravelInsuranceTypeEnum.SINGLE;
    }
  }

  const getTravelDestination = (so?: GetSoDto, soInitialData?: SoTravelTravelData): TravelDestinationEnum => {
    if (so?.productTypeSpecific?.travelData?.travelDestination) {
      return convertTravelDestinationBack(so?.productTypeSpecific?.travelData?.travelDestination);
    } else if (soInitialData?.travelDestination) {
      return convertTravelDestinationBack(soInitialData?.travelDestination);
    } else {
      return TravelDestinationEnum.EUROPE;
    }
  }

  const getLongestTripDuration = (so?: GetSoDto, soInitialData?: SoTravelTravelData): LongestTripDurationEnum => {
    if (so?.productTypeSpecific?.travelData?.longestTripDuration) {
      return convertLongestTripDurationBack(so?.productTypeSpecific?.travelData?.longestTripDuration);
    } else if (soInitialData?.longestTripDuration) {
      return convertLongestTripDurationBack(soInitialData?.longestTripDuration);
    } else {
      return LongestTripDurationEnum.LESS_THAN_THREE_MONTHS;
    }
  }

  const getLongestTripDurationDays = (so?: GetSoDto, soInitialData?: SoTravelTravelData): number => {
    if (so?.productTypeSpecific?.travelData?.longestTripDurationDays) {
      return so?.productTypeSpecific?.travelData?.longestTripDurationDays;
    } else if (soInitialData?.longestTripDurationDays) {
      return soInitialData?.longestTripDurationDays;
    } else {
      return 0;
    }
  }

  const getDefaultStartDate = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const modelToForm = (so?: GetSoDto, soInitialData?: SoTravelTravelData): BasicDataFormValues => ({
    insuranceType: getInsuranceType(so, soInitialData),
    insuranceStartDate: so?.productTypeSpecific?.travelData?.insuranceStartDate || soInitialData?.insuranceStartDate || getDefaultStartDate(),
    insuranceEndDate: so?.productTypeSpecific?.travelData?.insuranceEndDate || soInitialData?.insuranceEndDate || '',
    travelDestination: getTravelDestination(so, soInitialData),
    longestTripDuration: getLongestTripDuration(so, soInitialData),
    longestTripDurationDays: getLongestTripDurationDays(so, soInitialData).toString(),
  });

  const formToModel = (values: BasicDataFormValues): SoTravelTravelData => {
    const insuranceType = convertInsuranceType(values.insuranceType);
    return {
      insuranceType,
      insuranceStartDate: values.insuranceStartDate,
      insuranceEndDate: values.insuranceEndDate,
      travelDestination: convertTravelDestination(values.travelDestination),
      insuranceRange: so?.productTypeSpecific?.travelData?.insuranceRange || getDefaultCoverageRange(insuranceType),
      numOfInsuredAdults: so?.productTypeSpecific?.travelData?.numOfInsuredAdults || 1,
      numOfInsuredChildren: so?.productTypeSpecific?.travelData?.numOfInsuredChildren || 0,
      sportActivities: so?.productTypeSpecific?.travelData?.sportActivities || 'NONE' as any,
      willWorkOnTrip: so?.productTypeSpecific?.travelData?.willWorkOnTrip || false,
      numOfInsuredPersons: so?.productTypeSpecific?.travelData?.numOfInsuredPersons || 1,
      allInsuredPersonsAreFromTheSameFamily: so?.productTypeSpecific?.travelData?.allInsuredPersonsAreFromTheSameFamily || false,
      longestTripDuration: convertLongestTripDuration(values.longestTripDuration),
      longestTripDurationDays: parseInt(values.longestTripDurationDays) || 0,
      sportActivityCode: so?.productTypeSpecific?.travelData?.sportActivityCode || undefined,
      luggageValue: so?.productTypeSpecific?.travelData?.luggageValue || undefined,
      tripValue: so?.productTypeSpecific?.travelData?.tripValue || undefined,
    };
  };
  const basicDataFormValues: BasicDataFormValues = modelToForm(so as GetSoDto, soInitialData);

  if (loading) return null;

  return (
    <ContainerComponent>
      <Helmet>
        <title>Putnici | Putno osiguranje - kompare.hr</title>
      </Helmet>
      <Flex vertical gap={12}>
        <Card>
          <Flex vertical gap={32}>
            <Stepper currentStep={1} totalSteps={5} />
            <Flex vertical>
              <Text as="h1" type="xxLarge" bold>
                {i18n.t(`pages.basicData.title`)}
              </Text>
            </Flex>
            <Flex vertical gap={40}>
              <BasicDataForm ref={formRef} onSubmit={handleFormSubmit} basicData={basicDataFormValues} />
              {/* Appears only after the API asked for a captcha; solving it resubmits, so the button waits. */}
              <CaptchaChallenge {...captcha} />
              <Flex gap={24}>
                <Button
                  btnType="primary"
                  text={i18n.t("components.buttons.continue")}
                  style={{ width: "100%" }}
                  onClick={() => submitForm()}
                  disabled={captcha.required}
                >
                  <ArrowRightIcon color={colors.white} />
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Card>
        <RequestAgentCard onButtonClick={() => openCallMeModal()} />
      </Flex>
    </ContainerComponent>
  );
};

export default BasicDataPage;